// frontend/src/components/AdminOrders.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../api/client";
import { downloadInvoice } from "../utils/invoice";

const STATUS_OPTIONS = [
  { value: "created", label: "Created" },
  { value: "paid", label: "Paid" },
  { value: "accepted", label: "Accepted" },
  { value: "pending-shipping", label: "Pending Shipping" },
  { value: "shipped", label: "Shipped" },
  { value: "delivered", label: "Delivered" },
  { value: "cancelled", label: "Cancelled" },
  { value: "refunded", label: "Refunded" },
];

const STATUS_STYLES = {
  created: { bg: "#fff7ed", fg: "#9a3412", label: "Awaiting payment" },
  paid: { bg: "#dcfce7", fg: "#166534", label: "Paid" },
  accepted: { bg: "#dbeafe", fg: "#1d4ed8", label: "Accepted" },
  "pending-shipping": { bg: "#fef3c7", fg: "#92400e", label: "Packed" },
  shipped: { bg: "#e0e7ff", fg: "#3730a3", label: "Shipped" },
  delivered: { bg: "#dcfce7", fg: "#166534", label: "Delivered" },
  cancelled: { bg: "#fee2e2", fg: "#b91c1c", label: "Cancelled" },
  refunded: { bg: "#fef3c7", fg: "#a16207", label: "Refunded" },
};

const toDateInputValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

const getTrackingDraft = (order) => ({
  status: order.status || "created",
  courierName: order.shipment?.courierName || "",
  trackingNumber: order.shipment?.trackingNumber || "",
  trackingUrl: order.shipment?.trackingUrl || "",
  estimatedDelivery: toDateInputValue(order.shipment?.estimatedDelivery),
  supportNote: order.shipment?.supportNote || "",
  statusNote: "",
});

const AdminOrders = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "all");
  const [search, setSearch] = useState(searchParams.get("q") || "");
  const [sort, setSort] = useState(searchParams.get("sort") || "newest");
  const [openEditorId, setOpenEditorId] = useState("");
  const [drafts, setDrafts] = useState({});

  useEffect(() => {
    const adminToken = localStorage.getItem("admin-token");
    if (!adminToken) {
      navigate("/admin-login");
      return;
    }
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setStatusFilter(searchParams.get("status") || "all");
    setSearch(searchParams.get("q") || "");
    setSort(searchParams.get("sort") || "newest");
  }, [searchParams]);

  const setUrlState = (patch) => {
    const next = { status: statusFilter, q: search, sort, ...patch };
    const params = new URLSearchParams();
    if (next.status && next.status !== "all") params.set("status", next.status);
    if (next.q && next.q.trim()) params.set("q", next.q.trim());
    if (next.sort && next.sort !== "newest") params.set("sort", next.sort);
    setSearchParams(params);
  };

  const fetchOrders = async () => {
    setLoading(true);
    setErr("");
    try {
      const res = await api.get("/api/admin/orders");
      const data = res?.data?.orders || [];
      setOrders(data);
    } catch (e) {
      console.error("Fetch orders error:", e);
      setErr(e?.response?.data?.error || "Failed to fetch orders");
    } finally {
      setLoading(false);
    }
  };

  const saveOrderUpdate = async (id, payload) => {
    try {
      const res = await api.put(`/api/admin/orders/${id}/status`, payload);
      const updated = res?.data?.order;
      setOrders((prev) => prev.map((o) => (o._id === id ? updated || { ...o, ...payload } : o)));
      if (updated) {
        setDrafts((prev) => ({ ...prev, [id]: getTrackingDraft(updated) }));
      }
    } catch (e) {
      console.error("Update status error:", e);
      alert(e?.response?.data?.error || "Failed to update order status");
    }
  };

  const updateStatus = async (id, status) => {
    await saveOrderUpdate(id, { status });
  };

  const deleteOrder = async (id) => {
    if (!window.confirm("Delete this order?")) return;
    try {
      await api.delete(`/api/admin/orders/${id}`);
      setOrders((prev) => prev.filter((o) => o._id !== id));
    } catch (e) {
      console.error("Delete order error:", e);
      alert(e?.response?.data?.error || "Failed to delete order");
    }
  };

  const toggleEditor = (order) => {
    setOpenEditorId((prev) => (prev === order._id ? "" : order._id));
    setDrafts((prev) => ({
      ...prev,
      [order._id]: prev[order._id] || getTrackingDraft(order),
    }));
  };

  const updateDraft = (id, patch) => {
    setDrafts((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), ...patch },
    }));
  };

  const saveTrackingDetails = async (order) => {
    const draft = drafts[order._id] || getTrackingDraft(order);
    await saveOrderUpdate(order._id, {
      status: draft.status || order.status,
      courierName: draft.courierName,
      trackingNumber: draft.trackingNumber,
      trackingUrl: draft.trackingUrl,
      estimatedDelivery: draft.estimatedDelivery || null,
      supportNote: draft.supportNote,
      statusNote: draft.statusNote,
    });
  };

  const filteredOrders = useMemo(() => {
    const query = search.trim().toLowerCase();
    const rows = orders.filter((o) => {
      const pendingStatuses = ["created", "paid", "accepted", "pending-shipping"];
      const statusMatches =
        statusFilter === "all" ||
        o.status === statusFilter ||
        (statusFilter === "pending" && pendingStatuses.includes(o.status));
      const customer = o.customer || {};
      const haystack = [
        customer.name,
        customer.email,
        customer.phone,
        o.shipping?.phone,
        o.invoiceNumber,
        o.razorpay_order_id,
        ...(Array.isArray(o.items) ? o.items.map((row) => row.item?.name || "") : []),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      const searchMatches = !query || haystack.includes(query);
      return statusMatches && searchMatches;
    });
    return [...rows].sort((a, b) => {
      if (sort === "oldest") return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
      if (sort === "amount-high") return Number(b.amount || 0) - Number(a.amount || 0);
      if (sort === "amount-low") return Number(a.amount || 0) - Number(b.amount || 0);
      if (sort === "status") return String(a.status || "").localeCompare(String(b.status || ""));
      return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
    });
  }, [orders, search, sort, statusFilter]);

  const summary = useMemo(
    () => ({
      total: filteredOrders.length,
      revenue: filteredOrders.reduce((sum, order) => sum + Number(order.amount || 0), 0),
      pending: filteredOrders.filter((order) => ["created", "paid", "accepted", "pending-shipping"].includes(order.status)).length,
    }),
    [filteredOrders]
  );

  if (loading) return <p style={{ padding: 20 }}>Loading orders…</p>;
  if (err) return <p style={{ padding: 20, color: "red" }}>{err}</p>;

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ marginBottom: 16 }}>📦 All Orders</h2>

      <div style={summaryGrid}>
        {[
          ["Orders", summary.total],
          ["Revenue", `₹${Number(summary.revenue || 0).toFixed(2)}`],
          ["Pending", summary.pending],
        ].map(([label, value]) => (
          <button
            key={label}
            type="button"
            style={summaryCard}
            onClick={() => {
              if (label === "Pending") setUrlState({ status: "pending" });
              if (label === "Orders") setUrlState({ status: "all" });
            }}
          >
            <div style={{ color: "#64748b", fontSize: 12, fontWeight: 700, textTransform: "uppercase" }}>{label}</div>
            <div style={{ fontSize: 24, fontWeight: 900, marginTop: 8 }}>{value}</div>
          </button>
        ))}
      </div>

      <div style={toolbar}>
        <input
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setUrlState({ q: event.target.value });
          }}
          placeholder="Search customer, invoice, phone, item..."
          style={input}
        />
        <select value={statusFilter} onChange={(event) => setUrlState({ status: event.target.value })} style={input}>
          <option value="all">All Statuses</option>
          <option value="pending">Pending Workflow</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
        <select value={sort} onChange={(event) => setUrlState({ sort: event.target.value })} style={input}>
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
          <option value="amount-high">Amount high to low</option>
          <option value="amount-low">Amount low to high</option>
          <option value="status">Status A-Z</option>
        </select>
      </div>

      {filteredOrders.length === 0 ? (
        <p>No orders found.</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
            <thead style={{ background: "#f5f5f5" }}>
              <tr>
                <th style={th}>Customer</th>
                <th style={th}>Item</th>
                <th style={th}>Price</th>
                <th style={th}>Status</th>
                <th style={th}>Date</th>
                <th style={th}>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((o) => {
                const c = o.customer || {};
                const items = Array.isArray(o.items) && o.items.length ? o.items : (o.item ? [{ item: o.item, qty: 1, price: o.item.price }] : []);
                const badge = STATUS_STYLES[o.status] || STATUS_STYLES.created;
                const draft = drafts[o._id] || getTrackingDraft(o);
                return (
                  <React.Fragment key={o._id}>
                  <tr style={{ borderBottom: openEditorId === o._id ? "none" : "1px solid #e5e7eb" }}>
                    <td style={td}>
                      <strong>{c.name || "Unknown"}</strong>
                      <div style={{ fontSize: 12, color: "#6b7280" }}>
                        {c.email || "No email"}
                      </div>
                      <div style={{ fontSize: 12, color: "#6b7280" }}>
                        {o.shipping?.phone || c.phone || "No phone"}
                      </div>
                      <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                        {[o.shipping?.line1, o.shipping?.line2, o.shipping?.city, o.shipping?.state, o.shipping?.pincode].filter(Boolean).join(", ") || "Pickup / no address"}
                      </div>
                      {o.shipment?.trackingNumber ? (
                        <div style={{ fontSize: 12, color: "#0f766e", marginTop: 6 }}>
                          Tracking: {o.shipment.courierName ? `${o.shipment.courierName} • ` : ""}
                          {o.shipment.trackingNumber}
                        </div>
                      ) : null}
                    </td>
                    <td style={td}>
                      <div style={{ display: "grid", gap: 8 }}>
                        {items.map((row, index) => {
                          const product = row.item || {};
                          return (
                            <div key={`${o._id}-${index}`} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              {product.imageUrl ? (
                                <img
                                  src={product.imageUrl}
                                  alt={product.name || "item"}
                                  style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 6 }}
                                />
                              ) : null}
                              <div>
                                <div>{product.name || "Unknown"}</div>
                                <div style={{ fontSize: 12, color: "#6b7280" }}>
                                  Qty {row.qty} • Seller {product.sellerName || "Marketplace"}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </td>
                    <td style={td}>
                      <div>₹{Number(o.amount || 0).toFixed(2)}</div>
                      <div style={{ fontSize: 12, color: "#6b7280" }}>{o.invoiceNumber || o.razorpay_order_id}</div>
                      {o.shipment?.estimatedDelivery ? (
                        <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                          ETA {new Date(o.shipment.estimatedDelivery).toLocaleDateString()}
                        </div>
                      ) : null}
                    </td>
                    <td style={td}>
                      <div
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 8,
                          background: badge.bg,
                          color: badge.fg,
                          borderRadius: 999,
                          padding: "6px 10px",
                          fontWeight: 800,
                          fontSize: 12,
                          marginBottom: 8,
                        }}
                      >
                        {badge.label}
                      </div>
                      <select
                        value={o.status}
                        onChange={(e) => updateStatus(o._id, e.target.value)}
                        style={{ padding: 6, borderRadius: 6 }}
                      >
                        {STATUS_OPTIONS.map((s) => (
                          <option key={s.value} value={s.value}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td style={td}>{new Date(o.createdAt).toLocaleString()}</td>
                    <td style={td}>
                      <button
                        type="button"
                        onClick={() => toggleEditor(o)}
                        style={{
                          padding: "6px 10px",
                          background: openEditorId === o._id ? "#0f172a" : "#fff",
                          color: openEditorId === o._id ? "#fff" : "#0f172a",
                          border: "1px solid #cbd5e1",
                          borderRadius: 6,
                          cursor: "pointer",
                          marginRight: 8,
                          marginBottom: 8,
                        }}
                      >
                        {openEditorId === o._id ? "Close Tracking" : "Tracking"}
                      </button>
                      {o.notifications?.whatsappLink ? (
                        <a
                          href={o.notifications.whatsappLink}
                          target="_blank"
                          rel="noreferrer"
                          style={{ display: "inline-block", marginRight: 8, color: "#0f766e", fontWeight: 700 }}
                        >
                          WhatsApp
                        </a>
                      ) : null}
                      {Array.isArray(o.notifications?.sellerNotifications) &&
                      o.notifications.sellerNotifications.length ? (
                        <div style={{ display: "grid", gap: 6, marginBottom: 8 }}>
                          {o.notifications.sellerNotifications.map((note, index) =>
                            note.whatsappLink ? (
                              <a
                                key={`${o._id}-seller-${index}`}
                                href={note.whatsappLink}
                                target="_blank"
                                rel="noreferrer"
                                style={{ color: "#0f766e", fontWeight: 700, fontSize: 13 }}
                              >
                                Seller WhatsApp: {note.sellerName || "Seller"}
                              </a>
                            ) : null
                          )}
                        </div>
                      ) : null}
                      <button
                        onClick={() => downloadInvoice(o._id)}
                        style={{
                          padding: "6px 10px",
                          background: "#1d4ed8",
                          color: "white",
                          border: "none",
                          borderRadius: 6,
                          cursor: "pointer",
                          marginRight: 8,
                        }}
                      >
                        Invoice
                      </button>
                      <button
                        onClick={() => deleteOrder(o._id)}
                        style={{
                          padding: "6px 10px",
                          background: "#ef4444",
                          color: "white",
                          border: "none",
                          borderRadius: 6,
                          cursor: "pointer",
                        }}
                      >
                        🗑 Delete
                      </button>
                    </td>
                  </tr>
                  {openEditorId === o._id ? (
                    <tr style={{ borderBottom: "1px solid #e5e7eb", background: "#f8fbff" }}>
                      <td colSpan={6} style={{ padding: 18 }}>
                        <div style={editorGrid}>
                          <label style={field}>
                            <span style={labelStyle}>Workflow status</span>
                            <select
                              value={draft.status}
                              onChange={(event) => updateDraft(o._id, { status: event.target.value })}
                              style={input}
                            >
                              {STATUS_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label style={field}>
                            <span style={labelStyle}>Courier name</span>
                            <input
                              value={draft.courierName}
                              onChange={(event) => updateDraft(o._id, { courierName: event.target.value })}
                              placeholder="Delhivery, DTDC, local rider..."
                              style={input}
                            />
                          </label>
                          <label style={field}>
                            <span style={labelStyle}>Tracking number</span>
                            <input
                              value={draft.trackingNumber}
                              onChange={(event) => updateDraft(o._id, { trackingNumber: event.target.value })}
                              placeholder="Shipment or docket number"
                              style={input}
                            />
                          </label>
                          <label style={field}>
                            <span style={labelStyle}>Tracking link</span>
                            <input
                              value={draft.trackingUrl}
                              onChange={(event) => updateDraft(o._id, { trackingUrl: event.target.value })}
                              placeholder="https://..."
                              style={input}
                            />
                          </label>
                          <label style={field}>
                            <span style={labelStyle}>Estimated delivery</span>
                            <input
                              type="date"
                              value={draft.estimatedDelivery}
                              onChange={(event) => updateDraft(o._id, { estimatedDelivery: event.target.value })}
                              style={input}
                            />
                          </label>
                          <label style={field}>
                            <span style={labelStyle}>Status note for customer</span>
                            <input
                              value={draft.statusNote}
                              onChange={(event) => updateDraft(o._id, { statusNote: event.target.value })}
                              placeholder="Packed carefully for safe fish transport"
                              style={input}
                            />
                          </label>
                          <label style={{ ...field, gridColumn: "1 / -1" }}>
                            <span style={labelStyle}>Support note</span>
                            <textarea
                              value={draft.supportNote}
                              onChange={(event) => updateDraft(o._id, { supportNote: event.target.value })}
                              placeholder="Any special delivery note or support guidance"
                              style={{ ...input, minHeight: 84, resize: "vertical" }}
                            />
                          </label>
                        </div>
                        <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
                          <button
                            type="button"
                            onClick={() => saveTrackingDetails(o)}
                            style={primaryButton}
                          >
                            Save Tracking Details
                          </button>
                          {o.shipment?.trackingUrl ? (
                            <a href={o.shipment.trackingUrl} target="_blank" rel="noreferrer" style={secondaryLink}>
                              Open Tracking Link
                            </a>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ) : null}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const th = { padding: "12px 16px", borderBottom: "2px solid #e5e7eb", textAlign: "left" };
const td = { padding: "12px 16px", verticalAlign: "middle" };
const toolbar = {
  display: "grid",
  gridTemplateColumns: "minmax(260px, 1fr) 220px 220px",
  gap: 12,
  marginBottom: 16,
};
const input = {
  padding: "10px 12px",
  borderRadius: 12,
  border: "1px solid #cbd5e1",
  background: "#fff",
};
const summaryGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: 12,
  marginBottom: 16,
};
const summaryCard = {
  textAlign: "left",
  cursor: "pointer",
  background: "#fff",
  border: "1px solid #e2e8f0",
  borderRadius: 14,
  padding: 14,
  boxShadow: "0 10px 30px rgba(15,23,42,0.06)",
};
const editorGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: 12,
};
const field = {
  display: "grid",
  gap: 6,
};
const labelStyle = {
  fontSize: 12,
  fontWeight: 800,
  color: "#475569",
  textTransform: "uppercase",
};
const primaryButton = {
  padding: "10px 14px",
  borderRadius: 10,
  border: "none",
  background: "#0f766e",
  color: "#fff",
  cursor: "pointer",
  fontWeight: 800,
};
const secondaryLink = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "10px 14px",
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  color: "#0f172a",
  textDecoration: "none",
  background: "#fff",
  fontWeight: 700,
};

export default AdminOrders;
